const POSITIVE_TVOD = new Set(['PAID', 'COMPLETED', 'ACTIVE', 'APPROVED']);

function upper(value) {
  return String(value || '').trim().toUpperCase();
}

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function truthyFlag(value) {
  if (value === true) return true;
  const normalized = upper(value);
  return ['TRUE', 'YES', '1', 'ENABLED', 'ACTIVE'].includes(normalized);
}

function bearer(session) {
  const token = session?.access_token || '';
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function getJson(url, session) {
  const response = await fetch(url, {
    method: 'GET',
    headers: { accept: 'application/json', ...bearer(session) },
    cache: 'no-store',
  });
  let payload = null;
  try { payload = await response.json(); } catch (_) {}
  if (!response.ok) {
    const error = new Error(payload?.error || `${url} failed with ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return payload || {};
}

export function normalizeSubscription(payload = {}) {
  const status = upper(first(payload.status, payload.effective_status, payload.raw_status));
  const rawStatus = upper(first(payload.raw_status, payload.status, payload.effective_status));
  // `/api/subscription/me` is the server-side entitlement authority. A textual
  // PayPal status alone never grants access, but an explicit server access flag
  // remains authoritative even after auto-renew has been cancelled for a paid period.
  const active = payload.svod_active === true || payload.has_access === true;
  const cancelAtPeriodEnd =
    payload.cancel_at_period_end === true ||
    ['CANCELLED', 'CANCELED'].includes(rawStatus);
  return {
    active,
    adFree: active && payload.ad_free !== false,
    status: status || 'NONE',
    rawStatus: rawStatus || status || 'NONE',
    plan: String(first(payload.plan, payload.plan_id, '') || ''),
    interval: String(payload.interval || ''),
    accessUntil: first(payload.access_until, payload.current_period_end, payload.expires_at, null),
    paypalSubscriptionId: String(payload.paypal_subscription_id || ''),
    provider: String(first(payload.provider, payload.raw?.provider, '') || ''),
    cancelAtPeriodEnd,
    cancelledAt: first(payload.cancelled_at, payload.canceled_at, null),
    portalOnly: payload.portal_only === true || status === 'FILMMAKER_PORTAL_ONLY',
    source: String(payload.source || ''),
    raw: payload,
  };
}

function activeTvodItem(item = {}) {
  const status = upper(item.status);
  if (!POSITIVE_TVOD.has(status) && !['CANCELLED', 'CANCELED'].includes(status)) return false;
  const end = first(item.access_until, item.expires_at, item.current_period_end, item.grace_until, null);
  if (!end) return POSITIVE_TVOD.has(status);
  const millis = Date.parse(end);
  return Number.isFinite(millis) && millis > Date.now();
}

export function normalizeTvod(payload = {}) {
  const items = Array.isArray(payload.items) ? payload.items.filter(activeTvodItem) : [];
  const titleIds = new Set([
    ...(Array.isArray(payload.title_ids) ? payload.title_ids : []),
    ...items.map((item) => item.title_id),
  ].map((id) => String(id || '').trim()).filter(Boolean));
  return { items, titleIds, portalOnly: payload.portal_only === true, raw: payload };
}

export async function fetchViewerEntitlements(session) {
  if (!session?.user || !session?.access_token) {
    return {
      subscription: normalizeSubscription({ status: 'GUEST', svod_active: false, has_access: false }),
      tvod: normalizeTvod({ items: [], title_ids: [] }),
    };
  }

  const [subscriptionPayload, tvodPayload] = await Promise.all([
    getJson('/api/subscription/me', session),
    getJson('/api/tvod/me', session),
  ]);

  return {
    subscription: normalizeSubscription(subscriptionPayload),
    tvod: normalizeTvod(tvodPayload),
  };
}

function monetizationObject(raw) {
  const value = raw?.monetization;
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function accessTokens(raw) {
  const monet = monetizationObject(raw);
  const candidates = [
    typeof raw?.monetization === 'string' ? raw.monetization : '',
    raw?.accessMode, raw?.access_mode, raw?.access, raw?.paywall,
    raw?.accessType, raw?.access_type, raw?.monetizationType, raw?.monetization_type,
    raw?.adMode, raw?.ad_mode,
    monet.mode, monet.type, monet.access, monet.accessMode, monet.access_mode,
  ];
  return candidates.map(upper).filter(Boolean).join('|');
}

export function monetizationPolicy(item, parent = null) {
  const raw = item?.raw || item || {};
  const parentRaw = parent?.raw || parent || {};
  const combined = { ...parentRaw, ...raw };
  const monet = { ...monetizationObject(parentRaw), ...monetizationObject(raw) };
  const tokens = accessTokens(combined);

  const avod = truthyFlag(first(monet.avod?.enabled, monet.avod, combined.avod, combined.isAvod, combined.is_avod)) || /AVOD|FREE_WITH_ADS/.test(tokens);
  const svod = truthyFlag(first(monet.svod?.enabled, monet.svod, combined.svod, combined.isSvod, combined.is_svod)) || /SVOD|SUBSCRIPTION/.test(tokens);
  const tvodConfig = first(monet.tvod, combined.tvod, {});
  const tvod = truthyFlag(first(tvodConfig?.enabled, tvodConfig, combined.isTvod, combined.is_tvod)) || /TVOD|RENTAL|\bRENT\b/.test(tokens);

  const rentalPrice = Number(first(
    tvodConfig?.rentalPrice, tvodConfig?.rental_price, tvodConfig?.price,
    combined.rentalPrice, combined.rental_price, combined.tvodPrice, combined.tvod_price,
  ));
  const rentalWindowHours = Number(first(
    tvodConfig?.rentalWindowHours, tvodConfig?.rental_window_hours,
    combined.rentalWindowHours, combined.rental_window_hours, 48,
  ));

  return {
    avod,
    svod,
    tvod,
    tagged: avod || svod || tvod,
    rentalPrice: Number.isFinite(rentalPrice) && rentalPrice > 0 ? rentalPrice : null,
    rentalWindowHours: Number.isFinite(rentalWindowHours) && rentalWindowHours > 0 ? rentalWindowHours : 48,
    currency: String(first(tvodConfig?.currency, combined.currency, 'USD')).toUpperCase(),
    raw: combined,
  };
}

function hasRental(tvod, ...ids) {
  return ids.map((id) => String(id || '').trim()).filter(Boolean).some((id) => tvod?.titleIds?.has(id));
}

export function decideTitleAccess({ item, user, subscription, tvod, kind = 'content' }) {
  if (kind === 'trailer') return { allowed: true, adMode: 'none', reason: 'trailer' };
  const policy = monetizationPolicy(item);
  if (subscription?.portalOnly && !subscription?.active) {
    return { allowed: false, adMode: 'none', reason: 'filmmaker_portal_only', policy };
  }
  if (!policy.tagged) return { allowed: true, adMode: 'none', reason: 'open', policy };
  const rented = hasRental(tvod, item?.id, item?.slug);
  if (policy.avod) {
    // Verified paid access takes precedence on mixed-format AVOD titles.
    if (subscription?.active) return { allowed: true, adMode: 'none', reason: 'member_avod', policy };
    if (rented) return { allowed: true, adMode: 'none', reason: 'tvod', policy };
    return { allowed: true, adMode: 'avod', reason: 'avod', policy };
  }

  if (policy.svod && policy.tvod) {
    if (!user) return { allowed: false, adMode: 'none', reason: 'login_or_choose', policy };
    if (subscription?.active || rented) return { allowed: true, adMode: 'none', reason: subscription?.active ? 'svod' : 'tvod', policy };
    return { allowed: false, adMode: 'none', reason: 'svod_or_rent', policy };
  }
  if (policy.svod) {
    if (!user) return { allowed: false, adMode: 'none', reason: 'login', policy };
    return subscription?.active
      ? { allowed: true, adMode: 'none', reason: 'svod', policy }
      : { allowed: false, adMode: 'none', reason: 'upgrade', policy };
  }
  if (policy.tvod) {
    if (!user) return { allowed: false, adMode: 'none', reason: 'tvod', policy };
    return rented
      ? { allowed: true, adMode: 'none', reason: 'tvod', policy }
      : { allowed: false, adMode: 'none', reason: 'tvod', policy };
  }
  return { allowed: true, adMode: 'none', reason: 'open', policy };
}

export function decideEpisodeAccess({ series, episode, user, subscription, tvod, kind = 'content' }) {
  if (kind === 'trailer') return { allowed: true, adMode: 'none', reason: 'trailer' };
  const policy = monetizationPolicy(episode, series);
  if (subscription?.portalOnly && !subscription?.active) {
    return { allowed: false, adMode: 'none', reason: 'filmmaker_portal_only', policy };
  }
  if (!policy.tagged) return { allowed: true, adMode: 'none', reason: 'open', policy };
  const seasonKey = series?.id && episode?.season ? `${series.id}:season:${episode.season}` : '';
  const rented = hasRental(tvod, episode?.id, seasonKey, series?.id, series?.slug);
  if (policy.avod) {
    // Verified paid access takes precedence on mixed-format AVOD titles.
    if (subscription?.active) return { allowed: true, adMode: 'none', reason: 'member_avod', policy };
    if (rented) return { allowed: true, adMode: 'none', reason: 'tvod', policy };
    return { allowed: true, adMode: 'avod', reason: 'avod', policy };
  }

  if (policy.svod && policy.tvod) {
    if (!user) return { allowed: false, adMode: 'none', reason: 'login_or_choose', policy };
    if (subscription?.active || rented) return { allowed: true, adMode: 'none', reason: subscription?.active ? 'svod' : 'tvod', policy };
    return { allowed: false, adMode: 'none', reason: 'episode_or_season_rent', policy };
  }
  if (policy.svod) {
    if (!user) return { allowed: false, adMode: 'none', reason: 'login', policy };
    return subscription?.active
      ? { allowed: true, adMode: 'none', reason: 'svod', policy }
      : { allowed: false, adMode: 'none', reason: 'upgrade', policy };
  }
  if (policy.tvod) {
    return rented
      ? { allowed: true, adMode: 'none', reason: 'tvod', policy }
      : { allowed: false, adMode: 'none', reason: 'episode_or_season_rent', policy };
  }
  return { allowed: true, adMode: 'none', reason: 'open', policy };
}

export function accessMessage(decision, title = 'This title') {
  const messages = {
    filmmaker_portal_only: 'This account is approved for the WatchVIM Filmmaker Portal only and does not include streaming access.',
    login: `${title} is available with a WatchVIM membership. Log in or create an account to continue.`,
    login_or_choose: `Log in to watch ${title}, subscribe for membership access, or rent it individually.`,
    upgrade: `Your account does not have an active WatchVIM streaming membership. Subscribe to continue.`,
    svod_or_rent: `Subscribe for WatchVIM membership access or rent ${title} individually.`,
    tvod: `Rent ${title} to unlock playback.`,
    episode_or_season_rent: 'Subscribe, rent this episode, or rent the available season/title access to continue.',
  };
  return messages[decision?.reason] || 'Playback is not available for this account.';
}

